import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const BASE = "https://dailydish.in/api/admin/nudge-campaign";
const HUB_BASE = "https://dailydish.in/api/Hub";

// ── tiny style helpers ────────────────────────────────────────
const card = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 2px 12px #0001",
  padding: 24,
  marginBottom: 24,
};
const lbl = {
  fontWeight: 600,
  fontSize: 12,
  marginBottom: 3,
  display: "block",
  color: "#555",
};
const inp = {
  width: "100%",
  padding: "8px 11px",
  borderRadius: 8,
  border: "1px solid #ddd",
  fontSize: 13,
  marginBottom: 10,
  boxSizing: "border-box",
};
const btn = (color = "#1e4a1e", extra = {}) => ({
  background: color,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 18px",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  marginRight: 8,
  ...extra,
});

const SESSIONS = ["Breakfast", "Lunch", "Dinner"];

const emptyForm = {
  date: "",
  sendTime: "20:30",
  session: "Breakfast",
  hubIds: [],
  imageUrl: "",
  imageFile: null,
  previewUrl: "",
  customLine1: "",
  customLine2: "",
};

// ─────────────────────────────────────────────────────────────
const NudgeCampaignAdmin = () => {
  const today = new Date().toISOString().split("T")[0];

  // ── config (default fallback) ──────────────────────────────
  const [defaultImageUrl, setDefaultImageUrl] = useState("");
  const [defaultImageFile, setDefaultImageFile] = useState(null);
  const [defaultPreview, setDefaultPreview] = useState("");
  const [defaultLine1, setDefaultLine1] = useState("");
  const [defaultLine2, setDefaultLine2] = useState("");
  const [defaultSendTime, setDefaultSendTime] = useState("20:30");
  const [defaultSession, setDefaultSession] = useState("Breakfast");
  const [configSaving, setConfigSaving] = useState(false);

  // ── hubs ─────────────────────────────────────────────────────
  const [hubs, setHubs] = useState([]);
  const hubById = useMemo(() => {
    const m = new Map();
    hubs.forEach((h) => m.set(String(h._id), h));
    return m;
  }, [hubs]);

  // ── live schedule (from server) ──────────────────────────────
  const [schedule, setSchedule] = useState([]);

  // ── stats ────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);

  // ── create/edit form ────────────────────────────────────────
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eligiblePreview, setEligiblePreview] = useState(null);
  const [checkingEligible, setCheckingEligible] = useState(false);

  // ── broadcast (unconditional, everyone in hub) ───────────────
  const [broadcasts, setBroadcasts] = useState([]);
  const [bForm, setBForm] = useState(emptyForm);
  const [bEditingId, setBEditingId] = useState(null);
  const [bSaving, setBSaving] = useState(false);
  const [bCountPreview, setBCountPreview] = useState(null);
  const [bChecking, setBChecking] = useState(false);

  // ── misc ─────────────────────────────────────────────────────
  const [toast, setToast] = useState("");
  const [running, setRunning] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  // ── load ─────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      const [cfgRes, schedRes, statsRes, hubsRes] = await Promise.all([
        axios.get(`${BASE}/config`),
        axios.get(`${BASE}/schedule`),
        axios.get(`${BASE}/stats`),
        axios.get(`${HUB_BASE}/hubs`),
      ]);
      setDefaultImageUrl(cfgRes.data.defaultImageUrl || "");
      setDefaultPreview(cfgRes.data.defaultImageUrl || "");
      setDefaultLine1(cfgRes.data.defaultCustomLine1 || "");
      setDefaultLine2(cfgRes.data.defaultCustomLine2 || "");
      setDefaultSendTime(cfgRes.data.defaultSendTime || "20:30");
      setDefaultSession(cfgRes.data.defaultSession || "Breakfast");
      setSchedule(schedRes.data);
      setStats(statsRes.data);
      setHubs(hubsRes.data || []);
    } catch {
      showToast("Failed to load data");
    }

    // Broadcasts fetched separately so a missing/old backend route can't
    // block hubs and the rest of the panel from loading.
    try {
      const bcastRes = await axios.get(`${BASE}/broadcast`);
      setBroadcasts(bcastRes.data || []);
    } catch {
      setBroadcasts([]);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── save default config ──────────────────────────────────────
  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      let imgUrl = defaultImageUrl;
      if (defaultImageFile) {
        const fd = new FormData();
        fd.append("image", defaultImageFile);
        const r = await axios.post(`${BASE}/config/image`, fd);
        imgUrl = r.data.imageUrl;
        setDefaultImageUrl(imgUrl);
        setDefaultPreview(imgUrl);
        setDefaultImageFile(null);
      }
      await axios.put(`${BASE}/config`, {
        defaultImageUrl: imgUrl,
        defaultCustomLine1: defaultLine1,
        defaultCustomLine2: defaultLine2,
        defaultSendTime,
        defaultSession,
      });
      showToast("✅ Default config saved");
      loadAll();
    } catch {
      showToast("❌ Save failed");
    } finally {
      setConfigSaving(false);
    }
  };

  // ── form helpers ────────────────────────────────────────────
  const updateForm = (field, value) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleHub = (hubId) => {
    setForm((f) => {
      const has = f.hubIds.includes(hubId);
      return {
        ...f,
        hubIds: has
          ? f.hubIds.filter((h) => h !== hubId)
          : [...f.hubIds, hubId],
      };
    });
    setEligiblePreview(null);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setEligiblePreview(null);
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setForm({
      date: entry.date,
      sendTime: entry.sendTime || "20:30",
      session: entry.session || "Breakfast",
      hubIds: (entry.hubIds || []).map(String),
      imageUrl: entry.imageUrl || "",
      imageFile: null,
      previewUrl: entry.imageUrl || "",
      customLine1: entry.customLine1 || "",
      customLine2: entry.customLine2 || "",
    });
    setEligiblePreview(entry.lastEligibleCount ?? null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const checkEligible = async () => {
    setCheckingEligible(true);
    try {
      const r = await axios.post(`${BASE}/eligible-count`, {
        hubIds: form.hubIds,
      });
      setEligiblePreview(r.data.count);
    } catch {
      showToast("❌ Could not compute eligible count");
    } finally {
      setCheckingEligible(false);
    }
  };

  // ── save (create or edit) ────────────────────────────────────
  const saveEntry = async () => {
    if (!form.date) return showToast("Pick a date");
    if (!form.sendTime) return showToast("Pick a time");
    if (!form.imageUrl && !form.imageFile && !defaultImageUrl)
      return showToast(
        "Add an image — this template needs one, and no default image is set",
      );
    setSaving(true);
    try {
      const body = {
        date: form.date,
        sendTime: form.sendTime,
        session: form.session,
        hubIds: form.hubIds,
        imageUrl: form.imageUrl || null,
        customLine1: form.customLine1,
        customLine2: form.customLine2,
      };

      let entryId = editingId;
      if (editingId) {
        await axios.put(`${BASE}/schedule/${editingId}`, body);
      } else {
        const r = await axios.post(`${BASE}/schedule`, body);
        entryId =
          r.data.scheduledMessages[r.data.scheduledMessages.length - 1]._id;
      }

      if (form.imageFile && entryId) {
        const fd = new FormData();
        fd.append("image", form.imageFile);
        await axios.post(`${BASE}/schedule/${entryId}/image`, fd);
      }

      showToast(editingId ? "✅ Updated" : "✅ Scheduled");
      resetForm();
      loadAll();
    } catch (err) {
      showToast(err?.response?.data?.error || "❌ Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this scheduled message?")) return;
    try {
      await axios.delete(`${BASE}/schedule/${id}`);
      showToast("Deleted");
      if (editingId === id) resetForm();
      loadAll();
    } catch {
      showToast("❌ Delete failed");
    }
  };

  // ── manual run ───────────────────────────────────────────────
  const manualRun = async () => {
    if (!defaultImageUrl)
      return showToast(
        "Set & save a default image first — Run Now uses it, and the template requires an image",
      );
    if (
      !window.confirm(
        "Send the checkout reminder right now to all eligible non-offer users?",
      )
    )
      return;
    setRunning(true);
    try {
      const r = await axios.post(`${BASE}/run`);
      showToast(r.data.message || "Campaign started");
    } catch {
      showToast("❌ Failed");
    } finally {
      setRunning(false);
    }
  };

  const hubLabel = (h) =>
    `${h.hubName}${h.hubType ? ` (${h.hubType}${h.orderMode ? `, ${h.orderMode}` : ""})` : " — untagged"}`;

  // ── broadcast helpers ────────────────────────────────────────
  const bUpdateForm = (field, value) =>
    setBForm((f) => ({ ...f, [field]: value }));

  const bToggleHub = (hubId) => {
    setBForm((f) => {
      const has = f.hubIds.includes(hubId);
      return {
        ...f,
        hubIds: has
          ? f.hubIds.filter((h) => h !== hubId)
          : [...f.hubIds, hubId],
      };
    });
    setBCountPreview(null);
  };

  const bResetForm = () => {
    setBForm(emptyForm);
    setBEditingId(null);
    setBCountPreview(null);
  };

  const bStartEdit = (entry) => {
    setBEditingId(entry._id);
    setBForm({
      date: entry.date,
      sendTime: entry.sendTime || "20:30",
      session: entry.session || "Breakfast",
      hubIds: (entry.hubIds || []).map(String),
      imageUrl: entry.imageUrl || "",
      imageFile: null,
      previewUrl: entry.imageUrl || "",
      customLine1: entry.customLine1 || "",
      customLine2: entry.customLine2 || "",
    });
    setBCountPreview(entry.lastRecipientCount ?? null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const bCheckCount = async () => {
    if (bForm.hubIds.length === 0)
      return showToast("Select at least one hub first");
    setBChecking(true);
    try {
      const r = await axios.post(`${BASE}/broadcast-count`, {
        hubIds: bForm.hubIds,
      });
      setBCountPreview(r.data.count);
    } catch {
      showToast("❌ Could not compute recipient count");
    } finally {
      setBChecking(false);
    }
  };

  const bSaveEntry = async () => {
    if (!bForm.date) return showToast("Pick a date");
    if (!bForm.sendTime) return showToast("Pick a time");
    if (bForm.hubIds.length === 0)
      return showToast("Select at least one hub for a broadcast");
    if (!bForm.imageUrl && !bForm.imageFile && !defaultImageUrl)
      return showToast(
        "Add an image — this template needs one, and no default image is set",
      );
    setBSaving(true);
    try {
      const body = {
        date: bForm.date,
        sendTime: bForm.sendTime,
        session: bForm.session,
        hubIds: bForm.hubIds,
        imageUrl: bForm.imageUrl || null,
        customLine1: bForm.customLine1,
        customLine2: bForm.customLine2,
      };

      let entryId = bEditingId;
      if (bEditingId) {
        await axios.put(`${BASE}/broadcast/${bEditingId}`, body);
      } else {
        const r = await axios.post(`${BASE}/broadcast`, body);
        entryId =
          r.data.broadcastMessages[r.data.broadcastMessages.length - 1]._id;
      }

      if (bForm.imageFile && entryId) {
        const fd = new FormData();
        fd.append("image", bForm.imageFile);
        await axios.post(`${BASE}/broadcast/${entryId}/image`, fd);
      }

      showToast(bEditingId ? "✅ Broadcast updated" : "✅ Broadcast scheduled");
      bResetForm();
      loadAll();
    } catch (err) {
      showToast(err?.response?.data?.error || "❌ Save failed");
    } finally {
      setBSaving(false);
    }
  };

  const bDeleteEntry = async (id) => {
    if (!window.confirm("Delete this broadcast?")) return;
    try {
      await axios.delete(`${BASE}/broadcast/${id}`);
      showToast("Deleted");
      if (bEditingId === id) bResetForm();
      loadAll();
    } catch {
      showToast("❌ Delete failed");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh", padding: 24 }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.startsWith("✅")
              ? "#2e7d32"
              : toast.startsWith("❌")
                ? "#c62828"
                : "#1565c0",
            color: "#fff",
            padding: "12px 22px",
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 14,
            boxShadow: "0 4px 16px #0003",
          }}
        >
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800, color: "#1e4a1e", margin: 0 }}>
          📣 Checkout Reminder — Daily (non-offer users)
        </h2>
        <p style={{ color: "#888", margin: "4px 0 0", fontSize: 13 }}>
          Hub-wise, scheduled to a specific date &amp; time · Preorder hubs
          suppressed if ordered for tomorrow · Instant hubs suppressed if
          ordered for today · Registered ≥ 1 Jun 2026 · Excludes Employee-status
          users &amp; active free-offer users · Opts out after 10 reminders with
          zero orders ever
        </p>
      </div>

      {/* Stats + Run Now */}
      {stats && (
        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 24,
            flexWrap: "wrap",
            alignItems: "stretch",
          }}
        >
          {[
            ["Eligible Right Now (all hubs)", stats.totalEligible, "#1e4a1e"],
            ["Opted Out", stats.optedOut, "#b71c1c"],
            ["Excluded (active free offer)", stats.withActiveOffer, "#1565c0"],
          ].map(([l, v, c]) => (
            <div
              key={l}
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "14px 22px",
                boxShadow: "0 2px 8px #0001",
                borderLeft: `4px solid ${c}`,
                minWidth: 180,
              }}
            >
              <div style={{ fontSize: 11, color: "#888" }}>{l}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: c }}>
                {v ?? "—"}
              </div>
            </div>
          ))}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            <button
              onClick={manualRun}
              disabled={running}
              style={btn("#e65100")}
            >
              {running ? "Sending…" : "▶ Run Now (default, all hubs)"}
            </button>
          </div>
        </div>
      )}

      {/* ── Default fallback config ── */}
      <div style={card}>
        <h5 style={{ fontWeight: 700, marginBottom: 14, color: "#1e4a1e" }}>
          🖼 Default Fallback
          <span
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: "#888",
              marginLeft: 8,
            }}
          >
            used on any eligible day with no scheduled entry — fires once at the
            time below
          </span>
        </h5>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Image (URL or upload)</label>
            <input
              type="text"
              value={defaultImageUrl}
              onChange={(e) => {
                setDefaultImageUrl(e.target.value);
                setDefaultPreview(e.target.value);
              }}
              style={inp}
              placeholder="https://…"
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files[0];
                setDefaultImageFile(f);
                if (f) setDefaultPreview(URL.createObjectURL(f));
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Send Time (IST)</label>
            <input
              type="time"
              value={defaultSendTime}
              onChange={(e) => setDefaultSendTime(e.target.value)}
              style={{ ...inp, width: 160 }}
            />
            <label style={lbl}>Session ({"{{2}}"})</label>
            <select
              value={defaultSession}
              onChange={(e) => setDefaultSession(e.target.value)}
              style={{ ...inp, width: 160 }}
            >
              {SESSIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <label style={lbl}>
              Variable 3 (e.g. "Confirmation closes by 10:00 AM")
            </label>
            <input
              type="text"
              value={defaultLine1}
              onChange={(e) => setDefaultLine1(e.target.value)}
              style={inp}
              placeholder="Custom line 1"
            />
            <label style={lbl}>Variable 4</label>
            <input
              type="text"
              value={defaultLine2}
              onChange={(e) => setDefaultLine2(e.target.value)}
              style={inp}
              placeholder="Custom line 2"
            />
          </div>
          {defaultPreview && (
            <img
              src={defaultPreview}
              alt=""
              style={{
                width: 110,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
                border: "1px solid #eee",
              }}
            />
          )}
        </div>
        <button
          onClick={saveConfig}
          disabled={configSaving}
          style={{ ...btn(), marginTop: 8 }}
        >
          {configSaving ? "Saving…" : "Save Default"}
        </button>
      </div>

      {/* ── Create / Edit schedule entry ── */}
      <div style={card}>
        <h5 style={{ fontWeight: 700, marginBottom: 4, color: "#1e4a1e" }}>
          {editingId ? "✏️ Edit Scheduled Message" : "📅 Schedule a Message"}
        </h5>
        <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
          Pick any future date &amp; time and one or more hubs. Leave hubs
          unselected to target all eligible hubs.
        </p>

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div>
            <label style={lbl}>Date</label>
            <input
              type="date"
              min={today}
              value={form.date}
              onChange={(e) => {
                updateForm("date", e.target.value);
                setEligiblePreview(null);
              }}
              style={{ ...inp, width: 160, marginBottom: 0 }}
            />
          </div>
          <div>
            <label style={lbl}>Time (IST)</label>
            <input
              type="time"
              value={form.sendTime}
              onChange={(e) => updateForm("sendTime", e.target.value)}
              style={{ ...inp, width: 140, marginBottom: 0 }}
            />
          </div>
          <div>
            <label style={lbl}>Session ({"{{2}}"})</label>
            <select
              value={form.session}
              onChange={(e) => updateForm("session", e.target.value)}
              style={{ ...inp, width: 150, marginBottom: 0 }}
            >
              {SESSIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label style={lbl}>
          Hubs (
          {form.hubIds.length === 0
            ? "All eligible hubs"
            : `${form.hubIds.length} selected`}
          )
        </label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            padding: 12,
            border: "1px solid #eee",
            borderRadius: 8,
            marginBottom: 16,
            maxHeight: 160,
            overflowY: "auto",
            background: "#fafafa",
          }}
        >
          {hubs.length === 0 && (
            <span style={{ color: "#aaa", fontSize: 13 }}>Loading hubs…</span>
          )}
          {hubs.map((h) => (
            <label
              key={h._id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                background: form.hubIds.includes(String(h._id))
                  ? "#e8f5e9"
                  : "#fff",
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: h.hubType ? "pointer" : "not-allowed",
                opacity: h.hubType ? 1 : 0.5,
              }}
              title={
                h.hubType ? "" : "Untagged hub (hubType null) — never targeted"
              }
            >
              <input
                type="checkbox"
                disabled={!h.hubType}
                checked={form.hubIds.includes(String(h._id))}
                onChange={() => toggleHub(String(h._id))}
              />
              {hubLabel(h)}
            </label>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Image (URL or upload)</label>
            <input
              type="text"
              value={form.imageUrl}
              onChange={(e) => {
                updateForm("imageUrl", e.target.value);
                updateForm("previewUrl", e.target.value);
              }}
              style={inp}
              placeholder="https://… (blank = use default)"
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files[0];
                updateForm("imageFile", f);
                if (f) updateForm("previewUrl", URL.createObjectURL(f));
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Variable 3</label>
            <input
              type="text"
              value={form.customLine1}
              onChange={(e) => updateForm("customLine1", e.target.value)}
              style={inp}
              placeholder="e.g. Confirmation closes by 10:00 AM"
            />
            <label style={lbl}>Variable 4</label>
            <input
              type="text"
              value={form.customLine2}
              onChange={(e) => updateForm("customLine2", e.target.value)}
              style={inp}
              placeholder="Custom line 2 (blank = use default)"
            />
          </div>
          {form.previewUrl && (
            <img
              src={form.previewUrl}
              alt=""
              style={{
                width: 110,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
                alignSelf: "center",
              }}
            />
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button onClick={saveEntry} disabled={saving} style={btn()}>
            {saving
              ? "Saving…"
              : editingId
                ? "💾 Save Changes"
                : "💾 Schedule Message"}
          </button>
          {editingId && (
            <button onClick={resetForm} style={btn("#888")}>
              Cancel Edit
            </button>
          )}
          <button
            onClick={checkEligible}
            disabled={checkingEligible}
            style={btn("#1565c0")}
          >
            {checkingEligible ? "Checking…" : "👥 Check Eligible Count"}
          </button>
          {eligiblePreview !== null && (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1565c0" }}>
              {eligiblePreview} eligible user{eligiblePreview === 1 ? "" : "s"}{" "}
              will receive this
            </span>
          )}
        </div>
      </div>

      {/* ── Saved schedule table ── */}
      <div style={card}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            marginBottom: 10,
            color: "#333",
          }}
        >
          All Scheduled Messages ({schedule.length})
        </div>
        {schedule.length === 0 ? (
          <div
            style={{
              color: "#aaa",
              textAlign: "center",
              padding: 24,
              border: "1px dashed #ddd",
              borderRadius: 10,
            }}
          >
            No scheduled messages yet
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f0f7ee" }}>
                  {[
                    "Date",
                    "Time",
                    "Session",
                    "Hubs",
                    "Image",
                    "Message",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map((entry) => {
                  const isPast = entry.date < today;
                  const isToday = entry.date === today;
                  const hubNames = (entry.hubIds || []).length
                    ? entry.hubIds
                        .map((id) => hubById.get(String(id))?.hubName || "?")
                        .join(", ")
                    : "All hubs";
                  return (
                    <tr
                      key={entry._id}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        opacity: isPast && !entry.sentAt ? 0.5 : 1,
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.date}
                        {isToday && (
                          <span
                            style={{
                              marginLeft: 6,
                              background: "#2e7d32",
                              color: "#fff",
                              borderRadius: 5,
                              padding: "2px 6px",
                              fontSize: 10,
                            }}
                          >
                            TODAY
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        {entry.sendTime}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        {entry.session || "Breakfast"}
                      </td>
                      <td style={{ padding: "10px 12px", maxWidth: 200 }}>
                        {hubNames}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {entry.imageUrl ? (
                          <img
                            src={entry.imageUrl}
                            alt=""
                            style={{
                              width: 64,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        ) : (
                          <span style={{ color: "#aaa", fontSize: 12 }}>
                            default
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          maxWidth: 220,
                          fontSize: 12,
                          color: "#555",
                        }}
                      >
                        {entry.customLine1 || (
                          <em style={{ color: "#bbb" }}>default</em>
                        )}
                        {entry.customLine2 ? ` / ${entry.customLine2}` : ""}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {entry.sentAt ? (
                          <span
                            style={{
                              color: "#2e7d32",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            ✅ Sent
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "#e65100",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            ⏳ Pending
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        <button
                          onClick={() => startEdit(entry)}
                          disabled={!!entry.sentAt}
                          style={btn("#1565c0", {
                            padding: "6px 12px",
                            fontSize: 12,
                            opacity: entry.sentAt ? 0.4 : 1,
                          })}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteEntry(entry._id)}
                          style={btn("#c62828", {
                            padding: "6px 12px",
                            fontSize: 12,
                          })}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Broadcast (unconditional, everyone in hub) ── */}
      <div style={{ ...card, border: "2px solid #6a1b9a" }}>
        <h5 style={{ fontWeight: 700, marginBottom: 4, color: "#6a1b9a" }}>
          {bEditingId
            ? "✏️ Edit Broadcast"
            : "📢 Broadcast a Message (everyone in hub)"}
        </h5>
        <p style={{ color: "#888", fontSize: 13, marginBottom: 6 }}>
          Sends to <strong>every</strong> customer in the selected hub(s) — with
          or without an offer. Ignores opt-out, registration date, order
          history, Employee status and the reminder cap. At least one hub is
          required.
        </p>
        <div
          style={{
            background: "#f3e5f5",
            border: "1px solid #ce93d8",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            color: "#6a1b9a",
            marginBottom: 16,
          }}
        >
          ⚠ Unconditional send — there are no eligibility filters. Everyone in
          the chosen hub with a valid mobile receives this.
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div>
            <label style={lbl}>Date</label>
            <input
              type="date"
              min={today}
              value={bForm.date}
              onChange={(e) => {
                bUpdateForm("date", e.target.value);
                setBCountPreview(null);
              }}
              style={{ ...inp, width: 160, marginBottom: 0 }}
            />
          </div>
          <div>
            <label style={lbl}>Time (IST)</label>
            <input
              type="time"
              value={bForm.sendTime}
              onChange={(e) => bUpdateForm("sendTime", e.target.value)}
              style={{ ...inp, width: 140, marginBottom: 0 }}
            />
          </div>
          <div>
            <label style={lbl}>Session ({"{{2}}"})</label>
            <select
              value={bForm.session}
              onChange={(e) => bUpdateForm("session", e.target.value)}
              style={{ ...inp, width: 150, marginBottom: 0 }}
            >
              {SESSIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label style={lbl}>
          Hubs (
          {bForm.hubIds.length === 0
            ? "none selected — required"
            : `${bForm.hubIds.length} selected`}
          )
        </label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            padding: 12,
            border: "1px solid #eee",
            borderRadius: 8,
            marginBottom: 16,
            maxHeight: 160,
            overflowY: "auto",
            background: "#faf5fb",
          }}
        >
          {hubs.length === 0 && (
            <span style={{ color: "#aaa", fontSize: 13 }}>Loading hubs…</span>
          )}
          {hubs.map((h) => (
            <label
              key={h._id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                background: bForm.hubIds.includes(String(h._id))
                  ? "#f3e5f5"
                  : "#fff",
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={bForm.hubIds.includes(String(h._id))}
                onChange={() => bToggleHub(String(h._id))}
              />
              {hubLabel(h)}
            </label>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Image (URL or upload)</label>
            <input
              type="text"
              value={bForm.imageUrl}
              onChange={(e) => {
                bUpdateForm("imageUrl", e.target.value);
                bUpdateForm("previewUrl", e.target.value);
              }}
              style={inp}
              placeholder="https://… (blank = use default)"
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files[0];
                bUpdateForm("imageFile", f);
                if (f) bUpdateForm("previewUrl", URL.createObjectURL(f));
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Variable 3</label>
            <input
              type="text"
              value={bForm.customLine1}
              onChange={(e) => bUpdateForm("customLine1", e.target.value)}
              style={inp}
              placeholder="e.g. Confirmation closes by 10:00 AM"
            />
            <label style={lbl}>Variable 4</label>
            <input
              type="text"
              value={bForm.customLine2}
              onChange={(e) => bUpdateForm("customLine2", e.target.value)}
              style={inp}
              placeholder="Custom line 2 (blank = use default)"
            />
          </div>
          {bForm.previewUrl && (
            <img
              src={bForm.previewUrl}
              alt=""
              style={{
                width: 110,
                height: 80,
                objectFit: "cover",
                borderRadius: 8,
                alignSelf: "center",
              }}
            />
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={bSaveEntry}
            disabled={bSaving}
            style={btn("#6a1b9a")}
          >
            {bSaving
              ? "Saving…"
              : bEditingId
                ? "💾 Save Changes"
                : "💾 Schedule Broadcast"}
          </button>
          {bEditingId && (
            <button onClick={bResetForm} style={btn("#888")}>
              Cancel Edit
            </button>
          )}
          <button
            onClick={bCheckCount}
            disabled={bChecking}
            style={btn("#8e24aa")}
          >
            {bChecking ? "Checking…" : "👥 Check Recipient Count"}
          </button>
          {bCountPreview !== null && (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#6a1b9a" }}>
              {bCountPreview} user{bCountPreview === 1 ? "" : "s"} will receive
              this
            </span>
          )}
        </div>
      </div>

      {/* ── Saved broadcast table ── */}
      <div style={card}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            marginBottom: 10,
            color: "#6a1b9a",
          }}
        >
          All Broadcasts ({broadcasts.length})
        </div>
        {broadcasts.length === 0 ? (
          <div
            style={{
              color: "#aaa",
              textAlign: "center",
              padding: 24,
              border: "1px dashed #ddd",
              borderRadius: 10,
            }}
          >
            No broadcasts yet
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: "#f5eef7" }}>
                  {[
                    "Date",
                    "Time",
                    "Session",
                    "Hubs",
                    "Image",
                    "Message",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {broadcasts.map((entry) => {
                  const isPast = entry.date < today;
                  const isToday = entry.date === today;
                  const hubNames = (entry.hubIds || [])
                    .map((id) => hubById.get(String(id))?.hubName || "?")
                    .join(", ");
                  return (
                    <tr
                      key={entry._id}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        opacity: isPast && !entry.sentAt ? 0.5 : 1,
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.date}
                        {isToday && (
                          <span
                            style={{
                              marginLeft: 6,
                              background: "#6a1b9a",
                              color: "#fff",
                              borderRadius: 5,
                              padding: "2px 6px",
                              fontSize: 10,
                            }}
                          >
                            TODAY
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        {entry.sendTime}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        {entry.session || "Breakfast"}
                      </td>
                      <td style={{ padding: "10px 12px", maxWidth: 200 }}>
                        {hubNames}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {entry.imageUrl ? (
                          <img
                            src={entry.imageUrl}
                            alt=""
                            style={{
                              width: 64,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        ) : (
                          <span style={{ color: "#aaa", fontSize: 12 }}>
                            default
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          maxWidth: 220,
                          fontSize: 12,
                          color: "#555",
                        }}
                      >
                        {entry.customLine1 || (
                          <em style={{ color: "#bbb" }}>default</em>
                        )}
                        {entry.customLine2 ? ` / ${entry.customLine2}` : ""}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {entry.sentAt ? (
                          <span
                            style={{
                              color: "#2e7d32",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            ✅ Sent
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "#e65100",
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          >
                            ⏳ Pending
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: "10px 12px", whiteSpace: "nowrap" }}
                      >
                        <button
                          onClick={() => bStartEdit(entry)}
                          disabled={!!entry.sentAt}
                          style={btn("#6a1b9a", {
                            padding: "6px 12px",
                            fontSize: 12,
                            opacity: entry.sentAt ? 0.4 : 1,
                          })}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => bDeleteEntry(entry._id)}
                          style={btn("#c62828", {
                            padding: "6px 12px",
                            fontSize: 12,
                          })}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      <div
        style={{ ...card, background: "#f0f7ee", border: "1px solid #c8e6c9" }}
      >
        <h6 style={{ fontWeight: 700, color: "#1e4a1e", marginBottom: 10 }}>
          ℹ How it works
        </h6>
        <ul
          style={{
            margin: 0,
            paddingLeft: 20,
            fontSize: 13,
            color: "#444",
            lineHeight: 2.1,
          }}
        >
          <li>
            A background check runs every minute and fires each scheduled entry
            at its exact date &amp; time (IST) — future dates supported.
          </li>
          <li>
            Each entry targets specific hubs, or all eligible hubs if none are
            selected.
          </li>
          <li>
            Preorder hubs (apartments) are suppressed if the customer already
            has an order for <strong>tomorrow</strong>; instant hubs
            (schools/offices) are suppressed if they already have one for{" "}
            <strong>today</strong>.
          </li>
          <li>Hubs with no hub type tagged are never targeted.</li>
          <li>
            Only customers registered on/after <strong>1 Jun 2026</strong> are
            targeted.
          </li>
          <li>
            Customers with an active free-offer are excluded — they receive
            Checkout_new via the separate offer flow instead.
          </li>
          <li>
            Customers with status <strong>"Employee"</strong> (e.g. Digantara)
            are always excluded.
          </li>
          <li>
            A customer who has never placed an order is opted out after{" "}
            <strong>10</strong> reminders; once they place their first order,
            this cap no longer applies to them.
          </li>
          <li>
            Template: image header + <strong>Name</strong> ({"{{1}}"}) +{" "}
            <strong>Session</strong> ({"{{2}}"}) + two custom lines ({"{{3}}"},{" "}
            {"{{4}}"}) + a <strong>Confirm Now</strong> button that magic-logs
            the customer in and lands them home. Uses the approved{" "}
            <code>remindertocheckout_new</code> template.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default NudgeCampaignAdmin;
